//@flow
import * as Immutable from 'immutable';

import EntityTypeEnum from 'common/enums/EntityTypeEnum';

import { Contract } from 'common/models/flow/MasterContract';
import type { ContractModelType } from 'common/models/flow/MasterContract';

import { TransporterId } from './TransporterId';
import { VehicleModelId } from './VehicleModelId';
import { MasterVehicleModel } from './MasterVehicleModel';
import { MasterTransporter } from './MasterTransporter';
import { RiderPersonaId } from './RiderPersonaId';
import { TeamId } from './TeamId';
import { EmailAddress } from './EmailAddress';
import { AuditMetadata } from './AuditMetadata';
import { RiderPersona } from './MasterRiderPersona';
import { ContractCostFunction } from './ContractCostFunction';
import { ContractCostLookup } from './ContractCostLookup';
import { ExpiryNotification } from './ExpiryNotification';
import { ContractWorkflow } from './ContractWorkflow';

import { validatable, validate, validateId } from 'common/utils/ValidationUtil';

export const ContractTypeEnum = new EntityTypeEnum({
    VEHICLE: 'VEHICLE',
    RIDER: 'RIDER'
});

export const ContractStatusEnum = new EntityTypeEnum({
    DRAFT: 'DRAFT',
    READY: 'READY',
    VERIFY: 'VERIFY',
    VOID: 'VOID',
    EXPIRED: 'EXPIRED',
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE'
});

export const FleetTypeEnum = new EntityTypeEnum({
    CAPTIVE: 'CAPTIVE',
    THIRD_PARTY: 'THIRD_PARTY',
    ADHOC: 'ADHOC'
});

@validatable
class InternalContract extends Contract {
    static fromJS(json: ContractModelType): InternalContract {
        const state: Object = Object.assign({}, json);
        state.transporterId = TransporterId.fromJS(state.transporterId);
        state.transporter = MasterTransporter.fromJS(state.transporter);
        state.vehicleModelIds = Immutable.List(
            state.vehicleModelIds
        ).map(item => VehicleModelId.fromJS(item));
        state.vehicleModels = Immutable.List(state.vehicleModels).map(item =>
            MasterVehicleModel.fromJS(item)
        );
        state.riderPersonaIds = Immutable.List(
            state.riderPersonaIds
        ).map(item => RiderPersonaId.fromJS(item));
        state.riderPersonas = Immutable.List(state.riderPersonas).map(item =>
            RiderPersona.fromJS(item)
        );
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.emailAddresses = Immutable.List(state.emailAddresses).map(item =>
            EmailAddress.fromJS(item)
        );
        state.auditMetadata = AuditMetadata.fromJS(state.auditMetadata);
        state.customProperties = Immutable.Map(state.customProperties);
        state.nextEffectiveStatuses = Immutable.List(
            state.nextEffectiveStatuses
        );
        state.costFunctions = Immutable.List(state.costFunctions).map(item =>
            ContractCostFunction.fromJS(item)
        );
        state.contractCostLookup = Immutable.List(
            state.contractCostLookup
        ).map(item => ContractCostLookup.fromJS(item));
        state.expiryNotification = ExpiryNotification.fromJS(
            state.expiryNotification
        );
        state.agreementUrls = Immutable.List(state.agreementUrls);
        state.workflow = ContractWorkflow.fromJS(state.workflow);
        state.actions = Immutable.List(state.actions);
        state.actionDescriptions = Immutable.List(state.actionDescriptions);
        state.remarks = Immutable.List(state.remarks);
        state.notifiers = Immutable.List(state.notifiers);
        state.allowedEffectiveStatuses = Immutable.List(
            state.allowedEffectiveStatuses
        );
        return new this(Immutable.Map(state));
    }

    @validate
    setId(id: string): this {
        return super.setId(id);
    }

    validateId() {
        return validateId(this.id);
    }

    get transporterIds(): TransporterId {
        return this._state.get('transporterIds');
    }

    get transporters(): MasterTransporter {
        return this._state.get('transporters');
    }

    get vehicleModelIds(): Immutable.List<VehicleModelId> {
        return this._state.get('vehicleModelIds');
    }

    get vehicleModels(): Immutable.List<MasterVehicleModel> {
        return this._state.get('vehicleModels');
    }

    get riderPersonaIds(): Immutable.List<RiderPersonaId> {
        return this._state.get('riderPersonaIds');
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    get emailAddresses(): Immutable.List<EmailAddress> {
        return this._state.get('emailAddresses');
    }

    get auditMetadata(): AuditMetadata {
        return this._state.get('auditMetadata');
    }

    get transporterId(): TransporterId {
        return this._state.get('transporterId');
    }

    get transporter(): MasterTransporter {
        return this._state.get('transporter');
    }

    get riderPersonas(): Immutable.List<RiderPersona> {
        return this._state.get('riderPersonas');
    }

    get costFunctions(): Immutable.List<ContractCostFunction> {
        return this._state.get('costFunctions');
    }

    get contractCostLookup(): Immutable.List<ContractCostLookup> {
        return this._state.get('contractCostLookup');
    }

    get expiryNotification(): ExpiryNotification {
        return this._state.get('expiryNotification');
    }

    get workflow(): ContractWorkflow {
        return this._state.get('workflow');
    }
}

export { InternalContract as Contract };
